import React, { useEffect, useState, useContext } from "react";
import CustomButton from "../components/CustomButton";
import CreateNewProject from "../components/modals/CreateNewProject";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { RxDashboard } from "react-icons/rx";
import { CgNotes } from "react-icons/cg";
import { TbDeviceAnalytics } from "react-icons/tb";
import styles from "./Home.module.css";
import { PieChart, Pie } from "recharts";
import { LuUsers } from "react-icons/lu";
import { getOrganizationAnalytics } from "../api/Api";
import { OrganizationContext } from "../contexts/OrganizationContextProvider";
import CustomSkeleton from "../components/CustomSkeleton";
import { BsBoxSeam } from "react-icons/bs";
import { TiWaves } from "react-icons/ti";

function Home() {
  const [show, setShow] = useState(false);
  const handleOpenModel = () => setShow(true);
  const close = () => setShow(false);
  const [entityCounts, setEntityCounts] = useState({});
  const [paperAnalytics, setPaperAnalytics] = useState([]);
  const { data: organization, loading: orgLoading } =
    useContext(OrganizationContext);
  const [loading, setLoading] = useState(false);

  const organizationId = organization?.current_organization?.id;
  const members = organization?.current_organization?.members;
  console.log("members", members);

  // fetch organization analytics
  const fetchData = async (organizationId) => {
    if (!organizationId || orgLoading) return;
    setLoading(true);
    await getOrganizationAnalytics(organizationId)
      .then((response) => {
        setEntityCounts(response.data.entityCounts);
        setPaperAnalytics(response.data.paperAnalytics);
      })
      .catch((error) => {
        console.error("Error fetching organization analytics: ", error);
      })
      .finally(() => setLoading(false));
  };

  // fetch organization analytics on component mount and when organization data changes
  useEffect(() => {
    fetchData(organizationId);
  }, [organizationId, organization]);

  const isAdmin = members?.some(
    (member) => member.role.includes("admin") && member.role.includes("(you)")
  );

  return (
    <section id="home" className="p-2 p-sm-3 p-xl-4">
      {/* organization overview */}
      <div className="rounded-4 overflow-hidden bg-white">
        <Header
          title="Dashboard"
          buttonTitle="New Project"
          onButtonClick={handleOpenModel}
        />

        {/* count cards */}
        <div className="container">
          <div
            className={`row gap-3 gap-sm-5 p-3 pt-3 pt-sm-5 mt-3 ${styles.countCardContainer}`}
          >
            <div className="row col-sm d-flex gap-3 gap-sm-5">
              <ItemCard
                title="Projects"
                className={`${styles.projectCard} col`}
                count={entityCounts?.projects?.count}
                totalCount={
                  isAdmin
                    ? ""
                    : entityCounts?.projects?.totalCount ||
                      entityCounts?.projects?.count === 0
                    ? ""
                    : entityCounts?.projects?.count
                }
                icon={<BsBoxSeam size={60} />}
                loading={loading}
              />
              <ItemCard
                title="Workstreams"
                className={`${styles.workStreamCard} col`}
                count={entityCounts?.workstreams?.count}
                totalCount={
                  isAdmin
                    ? ""
                    : entityCounts?.workstreams?.totalCount ||
                      entityCounts?.workstreams?.count === 0
                    ? ""
                    : entityCounts?.workstreams?.count
                }
                icon={<TiWaves size={80} />}
                loading={loading}
              />
            </div>
            <div className="row col-sm d-flex gap-3 gap-sm-5">
              <ItemCard
                title="Papers"
                className={`${styles.paperCard} col`}
                count={entityCounts?.papers?.count}
                totalCount={
                  isAdmin
                    ? ""
                    : entityCounts?.papers?.totalCount ||
                      entityCounts?.papers?.count === 0
                    ? ""
                    : entityCounts?.papers?.count
                }
                icon={<CgNotes size={60} />}
                loading={loading}
              />
              <ItemCard
                title="Members"
                className={`${styles.memberCard} col`}
                count={members?.length || entityCounts?.members?.count}
                icon={<LuUsers size={60} />}
                loading={loading}
              />
            </div>
          </div>
        </div>

        {/* analytics */}
        <div className="d-flex align-items-center gap-3 p-2 px-3 mt-4">
          <TbDeviceAnalytics size={24} />
          <h6>Paper Analytics</h6>
        </div>

        {/* analytics chart */}
        <div className={`p-3 ${styles.analyticsChartContainer}`}>
          <div
            className={`row row-cols-auto gap-3 d-flex rounded-3 px-0 py-4 mb-4`}
          >
            {paperAnalytics?.map((item, index) => (
              <div
                key={index}
                className="d-flex flex-column align-items-center"
              >
                <AnalysisChart
                  total={item.total}
                  count={item.count}
                  status={item.status}
                  size={0.9}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* create new project popup */}
      <CreateNewProject show={show} handleClose={close} />
    </section>
  );
}

export default Home;

// This can be used to create a header component for the dashboard page.
export const Header = ({
  title = "Dashboard",
  icon = <RxDashboard size={23} />,
  buttonTitle = "New Project", // this is the title of the button
  buttonIcon = <AddRoundedIcon sx={{ width: 20 }} />, // this is the icon of the button
  onButtonClick, // this is the function that will be called when the button is clicked
  className,
  showButton = true,
  actions = null, // this is a component that can be passed as a prop to the header // dont use button title and button icon if you are using actions
}) => {
  return (
    <div className={`${styles.header} ${className} rounded-top-3`}>
      <div
        className={`d-flex flex-sm-row justify-content-between align-items-center p-3 p-md-4`}
      >
        <h5 className="d-flex mb-0 text-nowrap">
          <div className="d-flex align-items-center mx-3">{icon}</div>
          {title}
        </h5>
        <div>
          {actions}
          {showButton && (
            <CustomButton onClick={onButtonClick} icon={buttonIcon}>
              {buttonTitle}
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  );
};

//--------------------------------------------------------------------
// This is a custom card component for the dashboard page.
const ItemCard = ({ title, count, totalCount, icon, className, loading }) => {
  if (loading) {
    return (
      <div className={`col bg-danger text-white rounded-4 ${className}`}>
        <CustomSkeleton count={1} className="rounded-4 w-100 h-100 p-4" />
      </div>
    );
  }
  return (
    <div className={`bg-danger text-white rounded-4 p-4 ${className}`}>
      <h6 className="text-white">{title}</h6>
      <div className="d-flex gap-4 justify-content-between align-items-center">
        <h3 className="text-white">
          {count}
          {totalCount && <span className="h5">/{totalCount}</span>}
        </h3>
        <div className={`pt-3 ps-3 text-white rounded-4`}>{icon} </div>
      </div>
    </div>
  );
};

//----------------------------------------------------------
// this is a custom label component for the chart
const CustomLabel = ({ viewBox, count }) => {
  const { cx, cy } = viewBox;
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
      <tspan x={cx} dy="25" fill="#0F3040" fontSize="24" fontWeight="bold">
        {count}
      </tspan>
    </text>
  );
};

// this props are for the chart data only for the example
const AnalysisChart = ({ total, count, status, size }) => {
  const start = 240;
  const end = -60;

  let a =
    count === 0
      ? start
      : count === total
      ? end
      : ((total - count) / total) * 300 - 60;
  let remaining = total === 0 ? 1 : total - count || 1; // Ensuring no NaN issues

  const data = [
    {
      name: "blue",
      value: count,
      color: "#0987CF",
      startAngle: start,
      endAngle: a,
    },
    {
      name: "Gray",
      value: remaining,
      color: "#EBEBEB",
      startAngle: a,
      endAngle: end,
    },
  ];

  return (
    <div className="d-inline-flex flex-column justify-content-center">
      <PieChart width={180 * size} height={140 * size}>
        {data.map((entry, index) => (
          <Pie
            key={`slice-${index}`}
            data={[entry]}
            cx={90 * size}
            cy={70 * size}
            innerRadius={50 * size}
            outerRadius={65 * size}
            startAngle={entry.startAngle}
            endAngle={entry.endAngle}
            fill={entry.color}
            paddingAngle={0}
            dataKey="value"
            stroke="none"
          />
        ))}
        <svg width="250" height="250">
          <CustomLabel
            viewBox={{ cx: 95 * size, cy: 55 * size }}
            count={count}
          />
        </svg>
      </PieChart>
      <div className="p6 fw-semibold text-center">{status}</div>
    </div>
  );
};
